import { useCallback, useEffect, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import setup from '~/assets/icons/setup_2.svg';
import tickCircle from '~/assets/icons/tick_circle.svg';
import { ButtonColors } from '~/components/ui/Button';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useMouseButton, useObservable } from '~/rage';
import {
	IModalReplyDefault,
	KeyPressStates,
	ModalButtonColor,
	ModalType,
	MouseButton,
	VehicleTuningCategory,
	categoryByTuningSubcategory,
} from '~/shared';
import { CaseBanner } from '../../components/CaseBanner';
import { ColorSelect } from '../../components/ColorSelect';
import { ExteriorTuning } from '../../components/ExteriorTuning';
import { Head } from '../../components/Head';
import { Main } from '../../components/Main';
import { Paint } from '../../components/Paint';
import { SubcategoryMenu } from '../../components/SubcategoryMenu';
import { Tinting } from '../../components/Tinting';
import { Tuning } from '../../components/Tuning';
import { WheelSettings } from '../../components/WheelSettings';
import {
	useShopPreview,
	useTuningCategories,
	useVehiclePaintLoader,
} from '../../hooks';
import { TUNING_GROUP_CATEGORY } from '../../tuningGroups.model';
import {
	resetStore,
	setInterfaceHidden,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	getActiveVariant,
	getEffectiveTuningSelection,
} from '../../utils/resolveTuningShopVariant';
import { StyledContainer } from './TuningStudio.styled';

const TuningStudio = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector(
		(state) => state.tuningStudio.activeStage,
	);
	const { shopItems, nonExteriorCategories } = useTuningCategories();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const ownedFreeTuning = useObservable(
		rage.vehicleTuningShop.getOwnedFreeTuning(),
	);
	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const modalState = useObservable(rage.modal.getCurrentModal());

	const wheelSettingsVisible = useMemo(() => {
		return (
			activeStage === TuningStudioStage.MAIN &&
			activeIndex > 2 &&
			nonExteriorCategories[activeIndex - 3]?.id ===
				VehicleTuningCategory.WHEEL_SETTINGS
		);
	}, [activeStage, activeIndex, nonExteriorCategories]);

	const colorSelectVisible = useMemo(() => {
		return activeStage === TuningStudioStage.PAINT;
	}, [activeStage]);

	const shopPreview = useShopPreview();
	const { loadVehiclePaint } = useVehiclePaintLoader();

	useEffectOnce(() => {
		Promise.all([
			rage.vehicleTuningShop.fetchOwnedFreeTuning(),
			rage.vehicleTuningShop.fetchOwnedItemsSettings(),
		]).catch((err) => rage.notifications.showException(err));
	});

	const activeTuningCategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningCategory,
	);
	const activeTuningSubcategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningSubcategory,
	);
	const activeTuningGroup = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningGroup,
	);
	const activeVariants = useLayoutSelector(
		(state) => state.tuningStudio.activeVariants,
	);

	const [cameraFacingCategory, cameraFacingSubcategory] = useMemo(() => {
		if (
			activeStage === TuningStudioStage.SUBCATEGORY_MENU &&
			activeTuningGroup
		) {
			return [TUNING_GROUP_CATEGORY[activeTuningGroup], null] as const;
		}

		if (activeStage !== TuningStudioStage.TUNING_MENU) {
			return [null, null] as const;
		}

		if (activeTuningSubcategory) {
			return [
				categoryByTuningSubcategory[activeTuningSubcategory],
				activeTuningSubcategory,
			] as const;
		}

		return [activeTuningCategory, null] as const;
	}, [
		activeStage,
		activeTuningCategory,
		activeTuningGroup,
		activeTuningSubcategory,
	]);

	useEffect(() => {
		void rage.vehicleTuningShop
			.setCameraFacing(cameraFacingCategory, cameraFacingSubcategory)
			.catch((err) => rage.notifications.showException(err));
	}, [cameraFacingCategory, cameraFacingSubcategory]);

	const filteredShopItems = useMemo(() => {
		if (activeTuningSubcategory) {
			return shopItems.filter(
				(item) => item.subcategoryId === activeTuningSubcategory,
			);
		}
		if (activeTuningCategory) {
			return shopItems.filter(
				(item) => item.categoryId === activeTuningCategory,
			);
		}
		return [];
	}, [shopItems, activeTuningCategory, activeTuningSubcategory]);

	const ownedItem = useMemo(() => {
		const activeShop = filteredShopItems[activeIndex];
		if (!activeShop) return false;
		const { id } = getEffectiveTuningSelection(activeShop, activeVariants);
		const isFreeOwned =
			!!ownedFreeTuning &&
			(ownedFreeTuning.ownedItems.includes(id) ||
				ownedFreeTuning.ownedKits.includes(id));
		const hasDonate = donateItems.some((item) => item.id === id);
		const isSdkOwned = rage.vehicleTuningShop.getIsOwned(id);
		return isFreeOwned || hasDonate || isSdkOwned;
	}, [
		filteredShopItems,
		activeIndex,
		ownedFreeTuning,
		donateItems,
		activeVariants,
	]);

	const installedItem = useMemo(() => {
		const activeShop = filteredShopItems[activeIndex];
		if (!activeShop) return false;
		const variant = getActiveVariant(activeShop, activeVariants);
		return variant ? !!variant.isInstalled : !!activeShop.isInstalled;
	}, [filteredShopItems, activeIndex, activeVariants]);

	const handleInstallActiveItem = useCallback(() => {
		if (!ownedItem || modalState || interfaceHidden) {
			return;
		}

		const activeItem = filteredShopItems[activeIndex];
		if (!activeItem) {
			return;
		}

		const effective = getEffectiveTuningSelection(activeItem, activeVariants);
		const text = `Вы хотите установить "${effective.installLabel}" на ваш автомобиль?`;
		const isKit = activeItem.categoryId === VehicleTuningCategory.KITS;

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: text,
				acceptButton: {
					text: 'Установить',
					color: ModalButtonColor.Success,
				},
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result: IModalReplyDefault) => {
				if (result.isAccepted) {
					if (isKit) {
						rage.vehicleTuningShop.installOwnedKit(activeItem.id);
					} else {
						rage.vehicleTuningShop.installOwnedItem(effective.id);
					}
				}
			},
		);
	}, [
		ownedItem,
		modalState,
		interfaceHidden,
		filteredShopItems,
		activeIndex,
		activeVariants,
	]);

	const installCustomButtons = useMemo(() => {
		if (installedItem) {
			return [
				{
					color: ButtonColors.Select2,
					text: 'Запчасть установлена',
					icon: tickCircle,
					disabled: true,
					onClick: (): void => undefined,
				},
			];
		}
		if (!ownedItem) return undefined;
		return [
			{
				color: ButtonColors.Select2,
				text: 'Установить деталь',
				icon: setup,
				onClick: handleInstallActiveItem,
			},
		];
	}, [ownedItem, installedItem, handleInstallActiveItem]);

	const handleMouseButton = useCallback((state: KeyPressStates) => {
		if (state === KeyPressStates.Pressed) {
			dispatch(setInterfaceHidden(true));
		} else if (state === KeyPressStates.Unpressed) {
			dispatch(setInterfaceHidden(false));
		}
	}, []);

	useMouseButton(MouseButton.Right, handleMouseButton, {
		state: KeyPressStates.Both,
	});

	useEffectOnce(() => {
		loadVehiclePaint();

		return () => {
			dispatch(resetStore());
		};
	});

	return (
		<StyledContainer
			$hidden={interfaceHidden}
			onContextMenu={(e) => e.preventDefault()}
		>
			<Head />
			<CSSTransition
				in={!wheelSettingsVisible && !colorSelectVisible}
				classNames={'case-banner'}
				timeout={300}
				appear
			>
				<CaseBanner />
			</CSSTransition>
			{activeStage === TuningStudioStage.MAIN && <Main />}
			{activeStage === TuningStudioStage.EXTERIOR_MENU && <ExteriorTuning />}
			{activeStage === TuningStudioStage.SUBCATEGORY_MENU && (
				<SubcategoryMenu />
			)}
			{activeStage === TuningStudioStage.PAINT && <Paint />}
			{activeStage === TuningStudioStage.TINTING && <Tinting />}
			{activeStage === TuningStudioStage.TUNING_MENU && <Tuning />}
			<CSSTransition
				classNames="wheel-settings"
				in={wheelSettingsVisible}
				timeout={300}
				unmountOnExit
			>
				<WheelSettings />
			</CSSTransition>
			<CSSTransition
				classNames="color-select"
				in={colorSelectVisible}
				timeout={300}
				unmountOnExit
			>
				<ColorSelect />
			</CSSTransition>
			<ShopPreview
				style={{ top: 140, right: 60 }}
				{...shopPreview}
				visible={shopPreview.visible && !interfaceHidden}
				customButtons={installCustomButtons ?? shopPreview.customButtons}
			/>
		</StyledContainer>
	);
};

export { TuningStudio };
